import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PaymentOutlined from '@mui/icons-material/PaymentOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';

import PageHeader from '../components/common/PageHeader';
import StatusBadge from '../components/common/StatusBadge';
import {
  getConsultancies,
  createConsultancy,
  updateConsultancy,
  updateStudentPayout,
} from '../services/consultancyService';

const emptyForm = {
  name: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  accountNumber: '',
  ifscCode: '',
  bankName: '',
  accountHolderName: '',
  upiVpaId: '',
};

const ConsultancyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [consultancies, setConsultancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // Add/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(location.pathname === '/consultancies/new');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Payout dialog
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutStudent, setPayoutStudent] = useState(null);
  const [payoutData, setPayoutData] = useState({
    consultancyPayout: '',
    consultancyPayoutStatus: 'pending',
    consultancyPayoutDate: '',
    receipt: null,
  });
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getConsultancies();
      setConsultancies(res.data?.data?.consultancies || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load consultancies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return consultancies;
    const q = search.toLowerCase();
    return consultancies.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.contactPerson?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
    );
  }, [consultancies, search]);

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  // --- Add/Edit Consultancy ---
  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
    setDialogOpen(true);
  };

  const handleOpenEdit = (c) => {
    setEditingId(c._id);
    setFormData({
      name: c.name || '',
      contactPerson: c.contactPerson || '',
      phone: c.phone || '',
      email: c.email || '',
      address: c.address || '',
      accountNumber: c.accountNumber || '',
      ifscCode: c.ifscCode || '',
      bankName: c.bankName || '',
      accountHolderName: c.accountHolderName || '',
      upiVpaId: c.upiVpaId || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    try {
      setSaving(true);
      if (editingId) {
        await updateConsultancy(editingId, formData);
      } else {
        await createConsultancy(formData);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save consultancy');
    } finally {
      setSaving(false);
    }
  };

  // --- Payout Management ---
  const handleOpenPayout = (student) => {
    setPayoutStudent(student);
    setPayoutData({
      consultancyPayout: student.consultancyPayout || '',
      consultancyPayoutStatus: student.consultancyPayoutStatus || 'pending',
      consultancyPayoutDate: student.consultancyPayoutDate
        ? new Date(student.consultancyPayoutDate).toISOString().split('T')[0]
        : '',
      receipt: null,
    });
    setReceiptPreview(student.consultancyPayoutReceipt || null);
    setPayoutDialogOpen(true);
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPayoutData({ ...payoutData, receipt: file });
      setReceiptPreview(URL.createObjectURL(file));
    }
  };

  const handlePayoutSave = async () => {
    if (!payoutStudent) return;
    try {
      setPayoutSaving(true);
      await updateStudentPayout(payoutStudent._id, payoutData);
      setPayoutDialogOpen(false);
      setPayoutStudent(null);
      setReceiptPreview(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update payout');
    } finally {
      setPayoutSaving(false);
    }
  };

  const totalPayout = consultancies.reduce((sum, c) => sum + (c.totalPayout || 0), 0);
  const totalPaid = consultancies.reduce((sum, c) => sum + (c.paidPayout || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader title="Consultancy Tie-Ups">
        <Button variant="contained" startIcon={<AddRounded />} onClick={handleOpenAdd}>
          Add Consultancy
        </Button>
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by consultancy name, contact person, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: '#9CA3AF', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#F9FAFB' } }}
        />
      </Paper>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Total Consultancies
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#1F2937' }}>
            {consultancies.length}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Total Students
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#2C7BE5' }}>
            {consultancies.reduce((sum, c) => sum + (c.totalStudents || 0), 0)}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Total Payout
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#F59E0B' }}>
            {formatAmount(totalPayout)}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 150 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Paid / Pending
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#10B981' }}>
            {formatAmount(totalPaid)}
            <Typography component="span" sx={{ fontSize: '1rem', color: '#EF4444', ml: 1 }}>
              / {formatAmount(totalPayout - totalPaid)}
            </Typography>
          </Typography>
        </Paper>
      </Box>

      {/* Consultancy List */}
      {filtered.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: '#9CA3AF' }}>
            {search ? 'No consultancies match your search' : 'No consultancies added yet. Click "Add Consultancy" to get started.'}
          </Typography>
        </Paper>
      ) : (
        filtered.map((c) => (
          <Accordion key={c._id} sx={{ mb: 1.5, '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <AccordionSummary expandIcon={<ExpandMoreRounded />} sx={{ px: 3, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                <Avatar sx={{ bgcolor: c.isActive ? '#F59E0B' : '#9CA3AF', width: 40, height: 40, fontSize: '0.875rem', fontWeight: 700 }}>
                  {c.name?.charAt(0)?.toUpperCase() || 'C'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 180 }}>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1F2937', cursor: 'pointer', '&:hover': { color: '#2C7BE5' } }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/consultancies/${c._id}`); }}
                  >
                    {c.name}
                    {!c.isActive && (
                      <Chip label="Inactive" size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 20, bgcolor: '#FEE2E2', color: '#DC2626' }} />
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.25, flexWrap: 'wrap' }}>
                    {c.contactPerson && (
                      <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonOutlined sx={{ fontSize: 14 }} /> {c.contactPerson}
                      </Typography>
                    )}
                    {c.phone && (
                      <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneOutlined sx={{ fontSize: 14 }} /> {c.phone}
                      </Typography>
                    )}
                    {c.bankName && (
                      <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccountBalanceOutlined sx={{ fontSize: 14 }} /> {c.bankName}
                      </Typography>
                    )}
                    {c.linkedUsers && c.linkedUsers.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {c.linkedUsers.map((u) => (
                          <Chip
                            key={u._id}
                            label={u.email}
                            size="small"
                            sx={{
                              fontSize: '0.65rem',
                              height: 20,
                              bgcolor: u.isActive ? alpha('#0EA5E9', 0.1) : alpha('#9CA3AF', 0.1),
                              color: u.isActive ? '#0EA5E9' : '#9CA3AF',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<PersonOutlined sx={{ fontSize: '15px !important' }} />}
                    label={`${c.totalStudents || 0} Students`}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: alpha('#2C7BE5', 0.1), color: '#2C7BE5' }}
                  />
                  <Chip
                    label={`Payout: ${formatAmount(c.totalPayout)}`}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: alpha('#F59E0B', 0.1), color: '#F59E0B' }}
                  />
                  {c.pendingPayout > 0 && (
                    <Chip
                      label={`Pending: ${formatAmount(c.pendingPayout)}`}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: alpha('#EF4444', 0.1), color: '#EF4444' }}
                    />
                  )}
                  <Tooltip title="View Details">
                    <IconButton component="span" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/consultancies/${c._id}`); }}>
                      <OpenInNewOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Consultancy">
                    <IconButton component="span" size="small" onClick={(e) => { e.stopPropagation(); handleOpenEdit(c); }}>
                      <EditOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              {/* Banking Details Summary */}
              {(c.accountNumber || c.upiVpaId) && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', mb: 1 }}>
                    Banking Details
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {c.accountHolderName && (
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>Account Holder</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1F2937' }}>{c.accountHolderName}</Typography>
                      </Box>
                    )}
                    {c.accountNumber && (
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>Account No.</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1F2937' }}>{c.accountNumber}</Typography>
                      </Box>
                    )}
                    {c.ifscCode && (
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>IFSC Code</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1F2937' }}>{c.ifscCode}</Typography>
                      </Box>
                    )}
                    {c.bankName && (
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>Bank</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1F2937' }}>{c.bankName}</Typography>
                      </Box>
                    )}
                    {c.upiVpaId && (
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>UPI VPA</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1F2937' }}>{c.upiVpaId}</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}

              {c.students?.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>File No.</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Student Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Loan Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Payout</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Payout Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase' }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {c.students.map((s) => (
                        <TableRow
                          key={s._id}
                          hover
                          sx={{ '&:last-child td': { borderBottom: 0 } }}
                        >
                          <TableCell
                            sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2C7BE5', cursor: 'pointer' }}
                            onClick={() => navigate(`/students/${s._id}`)}
                          >
                            {s.fileNumber || '-'}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: '0.8rem', color: '#1F2937', fontWeight: 500, cursor: 'pointer' }}
                            onClick={() => navigate(`/students/${s._id}`)}
                          >
                            {s.name}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1F2937' }}>
                            {formatAmount(s.loanAmount)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={s.status} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#F59E0B' }}>
                            {formatAmount(s.consultancyPayout)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={s.consultancyPayoutStatus === 'paid' ? 'Paid' : 'Pending'}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                bgcolor: s.consultancyPayoutStatus === 'paid' ? alpha('#10B981', 0.1) : alpha('#F59E0B', 0.1),
                                color: s.consultancyPayoutStatus === 'paid' ? '#10B981' : '#F59E0B',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Manage Payout">
                              <IconButton size="small" color="primary" onClick={() => handleOpenPayout(s)}>
                                <PaymentOutlined sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ fontSize: '0.85rem', color: '#9CA3AF', textAlign: 'center', py: 2 }}>
                  No students linked to this consultancy
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Add/Edit Consultancy Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? 'Edit Consultancy' : 'Add New Consultancy'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Consultancy Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>

            {/* Banking Details Section */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountBalanceOutlined sx={{ fontSize: 20 }} /> Banking Details
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Account Holder Name"
                value={formData.accountHolderName}
                onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Account Number"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="IFSC Code"
                value={formData.ifscCode}
                onChange={(e) => setFormData({ ...formData, ifscCode: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="UPI VPA ID"
                placeholder="e.g. name@upi"
                value={formData.upiVpaId}
                onChange={(e) => setFormData({ ...formData, upiVpaId: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !formData.name.trim()}>
            {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payout Management Dialog */}
      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentOutlined /> Manage Payout
        </DialogTitle>
        <DialogContent>
          {payoutStudent && (
            <Box>
              {/* Student Info */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1F2937' }}>
                  {payoutStudent.name}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  {payoutStudent.fileNumber} &bull; Loan: {formatAmount(payoutStudent.loanAmount)}
                </Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Payout Amount"
                    type="number"
                    value={payoutData.consultancyPayout}
                    onChange={(e) => setPayoutData({ ...payoutData, consultancyPayout: e.target.value })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">INR</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Payout Status"
                    value={payoutData.consultancyPayoutStatus}
                    onChange={(e) => setPayoutData({ ...payoutData, consultancyPayoutStatus: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Payout Date"
                    type="date"
                    value={payoutData.consultancyPayoutDate}
                    onChange={(e) => setPayoutData({ ...payoutData, consultancyPayoutDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<UploadFileOutlined />}
                    sx={{ height: '56px', textTransform: 'none', justifyContent: 'flex-start' }}
                  >
                    {payoutData.receipt ? payoutData.receipt.name : 'Upload Receipt'}
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf"
                      onChange={handleReceiptChange}
                    />
                  </Button>
                </Grid>

                {/* Receipt Preview */}
                {receiptPreview && (
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 1.5, border: '1px solid #E5E7EB', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <ReceiptLongOutlined sx={{ fontSize: 16 }} /> Receipt Preview
                      </Typography>
                      <Box
                        component="img"
                        src={receiptPreview}
                        alt="Receipt"
                        sx={{ maxWidth: '100%', maxHeight: 250, objectFit: 'contain', borderRadius: 1 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPayoutDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handlePayoutSave} variant="contained" disabled={payoutSaving}>
            {payoutSaving ? 'Saving...' : 'Save Payout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultancyPage;
